import { FC, useMemo, useState } from 'react';

import { ManualRefundInput } from '@shared/api/giftOrder/giftOrder.interface';

// 오늘 날짜를 로컬 기준 YYYY-MM-DD로. (toISOString은 UTC라 자정 근처 KST에서 하루 밀림)
const todayLocal = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

interface ManualRefundCancelDialogProps {
  /** 전체취소면 true, 부분취소면 false */
  isFullCancel: boolean;
  /** 부분취소 시 선택 쿠폰 수 (안내 문구용) */
  selectedCount: number;
  /** 결제수단 문자열 (가상계좌 / 계좌 입금 등) — 안내 표기용 */
  method?: string;
  /** 서버/프론트가 계산한 취소 예상 환불금액 — 환불금액 pre-fill 및 불일치 경고 기준 */
  expectedRefundAmount?: number;
  /** 처리 중 여부 (상위에서 API 진행상태 전달) */
  isCancelling: boolean;
  /** YYYY-MM-DD 초기 이체일 (미지정 시 오늘 날짜) */
  defaultTransferDate?: string;
  onClose: () => void;
  /**
   * 취소 확정. 취소사유와 수동환불 내역을 함께 넘긴다.
   * 상위에서 cancelGiftOrder / partialCancelGiftOrder 호출 시 그대로 실어 보낸다.
   */
  onConfirm: (
    cancelReason: string,
    manualRefund: ManualRefundInput,
  ) => Promise<void>;
}

// 가상계좌·계좌 입금 결제 전용 취소(수동 환불) 모달.
// 부트페이 자동취소가 불가한 결제수단이라, 관리자가 실제 이체한 환불 내역
// (환불금액·이체일·메모)을 입력받아 같은 취소 엔드포인트로 함께 전달한다.
// 계좌번호(고객 개인정보)는 받지 않는다 — 서버가 입력 금액과 계산 금액을 대조한다.
export const ManualRefundCancelDialog: FC<ManualRefundCancelDialogProps> = ({
  isFullCancel,
  selectedCount,
  method,
  expectedRefundAmount,
  isCancelling,
  defaultTransferDate,
  onClose,
  onConfirm,
}) => {
  const [cancelReason, setCancelReason] = useState('');
  const [refundAmount, setRefundAmount] = useState(
    expectedRefundAmount != null ? String(expectedRefundAmount) : '',
  );
  const [transferDate, setTransferDate] = useState(
    defaultTransferDate || todayLocal(),
  );
  const [memo, setMemo] = useState('');

  const refundAmountNum = Number(refundAmount);
  const amountValid = refundAmount.trim() !== '' && refundAmountNum > 0;
  const amountMismatch =
    expectedRefundAmount != null &&
    amountValid &&
    refundAmountNum !== expectedRefundAmount;

  const canSubmit =
    cancelReason.trim() !== '' &&
    amountValid &&
    transferDate.trim() !== '' &&
    !isCancelling;

  const methodLabel = useMemo(() => method?.trim() || '가상계좌/입금', [method]);

  const handleConfirm = async () => {
    if (!canSubmit) return;
    await onConfirm(cancelReason.trim(), {
      refundAmount: refundAmountNum,
      transferDate: transferDate.trim(),
      memo: memo.trim() || undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div
        className="fixed inset-0 bg-black/40"
        onClick={() => !isCancelling && onClose()}
      />
      <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
        <div className="flex items-center gap-2">
          <span className="rounded-md bg-blue-100 px-2 py-0.5 text-xs font-bold text-blue-700">
            {methodLabel}
          </span>
          <h3 className="text-lg font-bold text-gray-900">
            {isFullCancel ? '전체취소 (수동환불)' : '부분취소 (수동환불)'}
          </h3>
        </div>

        <p className="mt-2 text-sm text-gray-600">
          {isFullCancel
            ? '주문 전체를 취소합니다.'
            : `선택한 쿠폰 ${selectedCount}건을 취소합니다.`}{' '}
          이 주문은 가상계좌/입금 결제라 자동환불이 되지 않습니다.{' '}
          <b>실제 계좌이체로 환불을 완료한 뒤</b> 아래 정보를 입력하세요.
        </p>

        {/* 취소 사유 */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700">
            취소 사유 <span className="text-red-500">*</span>
          </label>
          <textarea
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="취소 사유를 입력하세요"
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
            rows={2}
          />
        </div>

        {/* 환불(이체) 금액 */}
        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-700">
            환불 금액 <span className="text-red-500">*</span>
            {expectedRefundAmount != null && (
              <span className="ml-1 text-xs font-normal text-gray-400">
                (예상 취소금액 {expectedRefundAmount.toLocaleString()}원)
              </span>
            )}
          </label>
          <div className="relative mt-1">
            <input
              type="number"
              min={0}
              value={refundAmount}
              onChange={(e) => setRefundAmount(e.target.value)}
              placeholder="실제 이체한 금액"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-8 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
            />
            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
              원
            </span>
          </div>
          {amountMismatch && (
            <p className="mt-1 text-xs text-amber-600">
              ⚠ 입력 금액이 예상 취소금액과 다릅니다. 실제 이체액이 맞는지
              확인하세요.
            </p>
          )}
        </div>

        {/* 이체일 */}
        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-700">
            이체일 <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={transferDate}
            onChange={(e) => setTransferDate(e.target.value)}
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </div>

        {/* 메모 (선택) */}
        <div className="mt-3">
          <label className="block text-sm font-medium text-gray-700">
            메모 <span className="text-xs font-normal text-gray-400">(선택)</span>
          </label>
          <input
            type="text"
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="이체한 우리 통장 등"
            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </div>

        <div className="mt-5 flex gap-3">
          <button
            onClick={onClose}
            disabled={isCancelling}
            className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            닫기
          </button>
          <button
            onClick={handleConfirm}
            disabled={!canSubmit}
            className="flex-1 rounded-lg bg-blue-600 py-2.5 text-sm font-bold text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {isCancelling ? '처리중...' : '환불 완료 처리'}
          </button>
        </div>
      </div>
    </div>
  );
};
